<template>
   <div>
      <div v-if="!loaded">
         <SpinnerOverlay />
      </div>
      <b-container v-if="loaded">
         <div v-if="!displayIntro">
            <ExpensesHeader :expense-type="expenseType" />
         </div>
         <div v-if="displayIntro && !isRndig" key="intro-content">
            <PageTitle :title="title" />
            <p class="mb-3">
               <template v-if="hasQualifiesLink">
                  {{ introPrefix }}
                  <b-link id="nav-help" :to="{name: 'get-started-stage'}">qualifies</b-link>
                  {{ introSuffix }}
               </template>
               <template v-else>
                  {{ currentIntro }}
               </template>
            </p>

            <p id="manager-message-description" v-for="(paragraph, idx) in suppliesCopy" :key="idx">
               {{ paragraph }}
            </p>

            <div class="d-flex align-items-center justify-content-end">
               <b-button
                  id="btn-begin"
                  variant="success"
                  class="d-flex align-items-center"
                  @click="hideIntro"
                  style="font-size: 0.75rem"
               >
                  Begin
                  <b-icon-arrow-right-short class="ml-1" />
               </b-button>
            </div>
         </div>
         <div v-else>
            <slot />
            <div class="d-flex align-items-center justify-content-between mt-2">
               <b-button
                  class="d-flex align-items-center dark-button button bottom-btn mt-2"
                  variant="primary"
                  @click="displayIntro = true"
                  style="font-size: 0.75rem"
                  v-if="!isRndig"
               >
                  <b-icon-arrow-left-short class="mr-1" />
                  View Introduction
               </b-button>
               <b-button
                  id="btn-complete-assignments"
                  class="action-button"
                  variant="success"
                  v-if="!isSME && hasDeclaration"
                  @click="submit()"
                  :disabled="isMarkCompletedDisabled"
                  style="font-size: 0.75rem"
               >
                  Mark Section Complete<b-icon-check />
               </b-button>
            </div>
         </div>
      </b-container>
   </div>
</template>

<script>
import {StudyPeriod} from '@/store/models/study';
import {mapGetters} from 'vuex';
import SpinnerOverlay from '@/views/common/widgets/SpinnerOverlay.vue';
import ExpensesHeader from './ExpensesHeader.vue';
import {INTRO_CONFIG, TITLE_CONFIG, resolveAssignmentType} from '@/helpers/baseConfig';
import {
   getRelevantFlags,
   hasUnresolvedFlags as hasUnresolvedExpenseFlags,
   isSectionMarkCompletedDisabled,
} from '@/helpers/expenseCompletion';

export default {
   name: 'ExpensesIntro',
   components: {
      ExpensesHeader,
      SpinnerOverlay,
   },
   props: {
      expenseType: {
         type: String,
         required: true,
         validator: (value) => ['cloud', 'supplies', 'contractors', 'employees'].includes(value),
      },
   },
   data() {
      return {
         loaded: false,
         displayIntro: false,
         fields: [
            StudyPeriod.tableField(),
            {key: 'status', class: 'no-wrap', sortable: false},
            {key: 'actions', label: ''},
         ],
      };
   },

   computed: {
      ...mapGetters({
         activeStudyId: 'companies/activeStudyId',
         clientState: 'clientState',
         isSME: 'isSME',
         isRndig: 'isRndig',
         company: 'companies/currentCompany',
         isActiveStudyInSetupStage: 'companies/isActiveStudyInSetupStage',
         activeStudy: 'companies/activeStudy',
         flags: 'flags/flags',
         profile: 'profile',
      }),

      isCompleted() {
         return this.$store.getters[`${this.expenseType}/isCompleted`];
      },

      hasSectionAssignment() {
         return this.$store.getters[`${this.expenseType}/hasSectionAssignment`];
      },

      currentIntro() {
         return INTRO_CONFIG[this.expenseType];
      },

      title() {
         return TITLE_CONFIG[this.expenseType];
      },

      introParts() {
         return this.currentIntro.split('{QUALIFIES_LINK}');
      },

      hasQualifiesLink() {
         return this.introParts.length === 2;
      },

      introPrefix() {
         return this.introParts[0] ?? '';
      },

      introSuffix() {
         return this.introParts[1] ?? '';
      },

      seenIntro() {
         return this.clientState?.sectionIntros?.[this.expenseType];
      },

      flagCheckSection() {
         return this.$store.getters[`${this.expenseType}/flagCheckSection`];
      },

      suppliesCopy() {
         return this.company?.prompts?.[this.expenseType]?.split('\n') ?? [];
      },

      activeAssignments() {
         const assignmentType = resolveAssignmentType(this.expenseType);
         return this.activeStudy[assignmentType];
      },

      crossPeriodMergedStatuses() {
         return this.$store.getters[`${this.expenseType}/crossPeriodMergedStatuses`];
      },

      // Checked across every period, not just one -- this is the overview/cross-period
      // page (as opposed to ExpensesCard.vue's single-period page), and this feeds the
      // same "Mark Section Complete" gate rendered there, so the two must agree
      // regardless of which page the user is looking at.
      hasUnresolvedFlags() {
         const relevantFlags = getRelevantFlags({
            flags: this.flags,
            studyId: this.activeStudyId,
            checkSection: this.flagCheckSection,
            userId: this.profile.id,
            includeAllUsers: this.isRndig,
         });

         return hasUnresolvedExpenseFlags(relevantFlags, this.$constants().FlagStates);
      },

      declaration() {
         return this.$store.getters[`${this.expenseType}/declaration`];
      },

      hasDeclaration() {
         if (this.expenseType === 'supplies') {
            return ['ONE', 'MANY'].includes(this.declaration);
         }

         if (this.expenseType === 'employees') {
            // Employees have no declaration concept -- `employees/declaration` is
            // permanently null (see store/modules/employees.js) since every company
            // has employees. Don't gate this button's visibility on it.
            return true;
         }

         return this.declaration;
      },

      isMarkCompletedDisabled() {
         return isSectionMarkCompletedDisabled({
            crossPeriodMergedStatuses: this.crossPeriodMergedStatuses,
            isCompleted: this.isCompleted,
            hasUnresolvedFlags: this.hasUnresolvedFlags,
            allAssignmentsCompleted: this.activeAssignments.every(({completed}) => !!completed),
         });
      },
   },

   methods: {
      hideIntro() {
         if (!this.seenIntro) {
            this.$store.dispatch('setSectionIntro', {
               section: this.expenseType,
               value: true,
            });
         }
         this.displayIntro = false;
      },

      async submit() {
         const config = {
            companyId: this.$companyId,
            //assigneeId
         };
         try {
            await this.$store.dispatch(`${this.expenseType}/submit`, config);
         } catch (error) {
            console.error(error);
         }
      },

      async fetchData() {
         try {
            this.displayIntro = !this.seenIntro && !this.isRndig;
            this.loaded = false;

            await this.$store.dispatch('projects/loadProjects', {companyId: this.$companyId});
            if (this.expenseType === 'employees') {
               await this.$store.dispatch('activities/loadActivities', {
                  companyId: this.$companyId,
               });
            }
            await this.$store.dispatch('companies/loadCompany', {
               companyId: this.$companyId,
               force: false,
            });
            await this.$store.dispatch(`${this.expenseType}/loadAllAssignmentsByCompanyId`, {
               companyId: this.$companyId,
            });

            // An SME with no assignment in this section (e.g. no employees ever
            // assigned to them) shouldn't be able to load this page at all --
            // matches the nav-visibility requirement in stages.js. Checked here
            // (after assignments load, and on every entry point: initial mount,
            // expenseType change, and activeStudyId change) rather than only in
            // the activeStudyId watcher, so direct/initial navigation is covered
            // too, not just switching studies while already on the page.
            if (
               (this.isSME && !this.hasSectionAssignment) ||
               (this.isActiveStudyInSetupStage && !this.isRndig)
            ) {
               this.$router.push({name: 'Home'});
               return;
            }

            await this.$store.dispatch(`${this.expenseType}/loadData`, {
               companyId: this.$companyId,
            });

            if (this.expenseType === 'contractors') return;

            // No period filter -- this page isn't scoped to one period, and the
            // completion gate below needs to see unresolved flags across all of them.
            await this.$store.dispatch('flags/loadFlags', {
               companyId: this.$companyId,
               checkSection: this.flagCheckSection,
               studyId: this.activeStudyId,
            });
         } catch (e) {
            this.$store.commit('showAlert', {
               fallbackMsg: `Failed to load ${this.title.toLowerCase()} data`,
               seconds: 5,
            });
         } finally {
            this.loaded = true;
         }
      },
   },

   async created() {
      await this.fetchData();
   },

   watch: {
      async expenseType(newVal, oldVal) {
         if (!newVal || +newVal === +oldVal) {
            return;
         }
         await this.fetchData();
      },
      async activeStudyId(newVal, oldVal) {
         if (!newVal || +newVal === +oldVal) {
            return;
         }
         await this.fetchData();
      },
   },
};
</script>

<style lang="scss" scoped></style>
